import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  MapPin,
  Calendar,
  Share2,
  Bookmark,
  Navigation,
  Sparkles,
  Film,
  Bike,
  Utensils,
  ShoppingBag,
  Palette,
  Users,
  HeartHandshake,
  Store,
  Music,
  Coffee,
  Trees,
  Camera,
  Briefcase,
  GraduationCap,
  Search,
  Filter,
} from "lucide-react";
import Seo from "@/components/Seo";
import PilotDemoBanner from "@/components/PilotDemoBanner";
import { CtaBanner } from "@/components/CTA";

const HERO_IMG = "/assets/pittsburgh-hero.png";

export default function Community() {
  return (
    <div data-testid="community-page" className="bg-cream">
      <PilotDemoBanner />
      <Seo
        title="Explore Your Community | WHO — Warm Hand Off"
        description="Life after service is about more than accessing benefits — it's about building connections, discovering opportunities, and creating a meaningful life."
        path="/community"
      />

      {/* HERO */}
      <section
        className="relative min-h-[78vh] lg:min-h-[86vh] flex items-end overflow-hidden bg-navy-deep"
        data-testid="community-hero"
      >
        <div className="absolute inset-0" aria-hidden>
          <img
            src={HERO_IMG}
            alt="Veterans, professionals, and family members overlooking the Pittsburgh skyline at golden hour."
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/80 to-navy-deep/25" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/70 via-transparent to-transparent" />
        </div>

        <div className="relative w-full max-w-[1240px] mx-auto px-6 lg:px-10 pb-16 lg:pb-24 pt-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <p className="eyebrow text-gold" data-testid="community-hero-eyebrow">
              Explore
            </p>
            <h1
              className="mt-4 font-display font-black text-cream tracking-tight leading-[0.98] text-5xl sm:text-6xl lg:text-7xl"
              data-testid="community-hero-title"
            >
              Explore Your <span className="text-gold-light">Community</span>.
            </h1>
            <p
              className="mt-6 text-lg lg:text-xl text-cream/85 leading-relaxed max-w-2xl"
              data-testid="community-hero-lede"
            >
              Life after service is about more than accessing benefits &mdash; it&rsquo;s about
              building connections, discovering opportunities, and creating a meaningful life.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href="#discovery"
                data-testid="community-hero-primary-cta"
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-6 py-3.5 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep"
              >
                <MapPin className="w-4 h-4" strokeWidth={2.5} /> Explore Near Me
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </a>
              <a
                href="#events"
                data-testid="community-hero-secondary-cta"
                className="inline-flex items-center gap-2 border border-cream/40 hover:border-gold hover:text-gold text-cream font-semibold px-6 py-3.5 rounded-md transition-colors"
              >
                <Calendar className="w-4 h-4" strokeWidth={2.5} /> View Upcoming Events
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CONCEPT MOCKUP — full-visual overview */}
      <section
        className="relative py-16 lg:py-20 bg-cream border-t border-borderSubtle"
        data-testid="community-mockup-section"
      >
        <div className="max-w-[1360px] mx-auto px-6 lg:px-10">
          <div className="max-w-[820px] mb-10">
            <p className="eyebrow text-gold-deep">The full experience</p>
            <h2 className="mt-3 font-display font-black text-navy tracking-tight leading-[1.05] text-3xl sm:text-4xl">
              A single place to <span className="text-gold-deep">discover, save, and show up</span>.
            </h2>
            <div className="mt-4 h-[3px] w-14 bg-gold rounded-full" aria-hidden />
            <p className="mt-5 text-lg text-slateSoft leading-relaxed">
              From daily recommendations to categories, live events, and an interactive community
              map &mdash; everything designed to help you find your place and stay connected.
            </p>
          </div>

          <motion.figure
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6 }}
            className="rounded-2xl overflow-hidden border border-borderSubtle shadow-2xl bg-navy-deep"
            data-testid="community-mockup-figure"
          >
            <img
              src="/assets/community-mockup.png"
              alt="Explore Your Community dashboard overview showing hero with 'Explore Near Me' and 'View Upcoming Events' CTAs, a 'More Than Resources. A Community.' category grid (Entertainment, Recreation & Leisure, Events & Happenings, Veteran-Owned Businesses, Retail & Discounts, Dining, Arts & Culture, Community Organizations), a 'What's Happening Near You Today?' events dashboard with filters (Today, This Week, Family Friendly, Free, Veteran Events, Indoor, Outdoor) and event cards (Pirates Military Appreciation Night, Veterans Hiking Group, Coffee with Veterans, Entrepreneurship Workshop, Farmers Market, Veteran-Owned Restaurant, Pittsburgh Jazz Festival, Volunteer at the Food Bank, Steelers Alumni Military Event, Bonsai Workshop), a WHO Guides recommendation panel with personalized picks for Marlon, an interactive Pittsburgh community map with pins for Veteran Organizations, Restaurants, Healthcare, Entertainment, Events, Shopping, Parks, Volunteer Opportunities, Training Centers, and Small Businesses, a 'Building Stronger Communities' section, and a footer promise with the WHO tagline 'Survive. Stabilize. Thrive. Belong.' and CTAs to Explore Your Community, Find Resources, and Join WHO."
              className="block w-full h-auto"
              loading="lazy"
              data-testid="community-mockup-image"
            />
            <figcaption className="px-6 py-4 bg-navy-deep border-t border-white/10 text-xs uppercase tracking-widest text-cream/60 font-semibold">
              Concept preview &middot; Explore Your Community
            </figcaption>
          </motion.figure>
        </div>
      </section>

      {/* SECTION 1 — Community Life */}
      <section className="py-24 lg:py-28 bg-cream border-t border-borderSubtle" data-testid="community-life-section">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="max-w-[820px]">
            <p className="eyebrow text-gold-deep">A community, not a directory</p>
            <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.03] text-4xl sm:text-5xl">
              More Than Resources. <span className="text-gold-deep">A Community.</span>
            </h2>
            <div className="mt-5 h-[3px] w-16 bg-gold rounded-full" aria-hidden />
            <p className="mt-6 text-lg text-slateSoft leading-relaxed">
              WHO helps people find not only the services they need, but also the experiences,
              events, businesses, and organizations that help them build relationships and feel
              connected.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="community-life-cards">
            {communityLifeCards.map((c) => (
              <LifeCard key={c.title} {...c} />
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 — Personalized Discovery */}
      <section
        className="relative py-24 lg:py-28 bg-navy-deep text-cream border-t border-navy-800 overflow-hidden"
        data-testid="community-discovery-section"
        id="discovery"
      >
        <div className="absolute inset-0 pointer-events-none opacity-40" aria-hidden>
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold/8 rounded-full blur-3xl -translate-y-1/3" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-cream/[0.04] rounded-full blur-3xl translate-y-1/3" />
        </div>

        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <p className="eyebrow text-gold">Personalized discovery</p>
              <h2 className="mt-4 font-display font-black text-cream tracking-tight leading-[1.03] text-4xl sm:text-5xl">
                What&rsquo;s Happening <span className="text-gold-light">Near You</span> Today?
              </h2>
              <p className="mt-6 text-cream/75 text-lg leading-relaxed">
                A live dashboard curated by <span className="text-gold-light font-semibold">WHO Guides&trade;</span>{" "}
                &mdash; recommendations tuned to your interests, distance, and time.
              </p>
            </div>
            <div className="flex items-center gap-2 bg-white/[0.06] border border-white/12 rounded-md px-4 py-2.5 text-sm text-cream/80" data-testid="discovery-search">
              <Search className="w-4 h-4 text-gold" />
              <span>Downtown Pittsburgh · 15 miles</span>
            </div>
          </div>

          {/* Filter chips */}
          <div className="flex flex-wrap gap-2 mb-8" data-testid="discovery-filters">
            {discoveryFilters.map((f, i) => (
              <button
                key={f}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                  i === 0
                    ? "bg-gold text-navy border-gold"
                    : "bg-white/[0.05] text-cream/80 border-white/12 hover:bg-white/10 hover:text-cream"
                }`}
                data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {i === 0 && <Filter className="w-3.5 h-3.5" />}
                {f}
              </button>
            ))}
          </div>

          {/* Event cards grid */}
          <div id="events" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="discovery-cards">
            {discoveryCards.map((e) => (
              <EventCard key={e.title} {...e} />
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — Community Categories */}
      <section className="py-24 lg:py-28 bg-cream-panel border-t border-borderSubtle" data-testid="community-categories-section">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="max-w-[820px]">
            <p className="eyebrow text-gold-deep">Explore by category</p>
            <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.03] text-4xl sm:text-5xl">
              Everything <span className="text-gold-deep">your community</span> has to offer.
            </h2>
            <div className="mt-5 h-[3px] w-16 bg-gold rounded-full" aria-hidden />
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="categories-grid">
            {categoryGroups.map((g) => (
              <CategoryGroup key={g.title} {...g} />
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — WHO Guides AI Recommendations */}
      <section className="py-24 lg:py-28 bg-cream border-t border-borderSubtle" data-testid="community-ai-section">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="eyebrow text-gold-deep">Powered by WHO Guides&trade;</p>
            <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.03] text-4xl sm:text-5xl">
              WHO Guides&trade; learns <span className="text-gold-deep">what matters to you</span>.
            </h2>
            <div className="mt-5 h-[3px] w-16 bg-gold rounded-full" aria-hidden />
            <p className="mt-6 text-lg text-slateSoft leading-relaxed">
              Every WHO Guide is a real person supported by AI. Over time, the assistant understands
              your interests, your rhythm, and the pace at which you want to reconnect &mdash;
              surfacing the events, businesses, and people most worth your time.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/experience"
                data-testid="ai-see-experience-btn"
                className="inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-cream font-semibold px-5 py-3 rounded-md transition-colors"
              >
                Meet WHO Guides <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Assistant mockup */}
          <div className="lg:col-span-7">
            <div
              className="rounded-2xl bg-navy-deep border border-white/10 shadow-2xl overflow-hidden"
              data-testid="ai-assistant-mockup"
            >
              <div className="flex items-center gap-2 px-5 py-3 border-b border-white/10">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-400/60" />
                <span className="ml-3 text-xs uppercase tracking-widest text-cream/50">
                  WHO Guides · Recommendations
                </span>
              </div>

              <div className="p-6 lg:p-8">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gold/20 flex items-center justify-center border border-gold/40">
                    <Sparkles className="w-4 h-4 text-gold" />
                  </div>
                  <p className="text-cream text-lg font-display font-semibold">Good morning, Marlon.</p>
                </div>
                <p className="mt-2 text-cream/70">Based on your interests, here are today&rsquo;s picks:</p>

                <ul className="mt-6 space-y-3" data-testid="ai-recommendations-list">
                  {aiRecommendations.map((r, i) => (
                    <li
                      key={r.title}
                      className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-white/[0.04] border border-white/8 hover:border-gold/40 transition-colors"
                      data-testid={`ai-rec-${i + 1}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-md bg-gold/15 border border-gold/30 flex items-center justify-center flex-shrink-0">
                          <r.icon className="w-4 h-4 text-gold" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-cream font-medium truncate">{r.title}</p>
                          <p className="text-xs text-cream/55">{r.meta}</p>
                        </div>
                      </div>
                      <span className="text-[10px] uppercase tracking-widest text-gold/90 font-bold whitespace-nowrap">
                        {r.tag}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-5 py-3 rounded-md transition-colors"
                  data-testid="ai-see-more-btn"
                >
                  See More Recommendations <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5 — Interactive Community Map */}
      <section className="py-24 lg:py-28 bg-cream-panel border-t border-borderSubtle" data-testid="community-map-section">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="max-w-[820px]">
            <p className="eyebrow text-gold-deep">Community map</p>
            <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.03] text-4xl sm:text-5xl">
              See <span className="text-gold-deep">every connection</span>, on one map.
            </h2>
            <div className="mt-5 h-[3px] w-16 bg-gold rounded-full" aria-hidden />
            <p className="mt-6 text-lg text-slateSoft leading-relaxed">
              Filter by category to reveal veteran organizations, veteran-owned businesses,
              healthcare, dining, parks, events, and volunteer opportunities near you.
            </p>
          </div>

          {/* Map mockup */}
          <div
            className="mt-10 relative rounded-2xl overflow-hidden border border-borderSubtle bg-navy-deep shadow-xl"
            data-testid="community-map-mockup"
          >
            <MapMockup />

            {/* Filter legend */}
            <div className="absolute top-4 left-4 right-4 sm:left-6 sm:right-auto sm:max-w-xs bg-white/95 backdrop-blur rounded-lg border border-borderSubtle p-4 shadow-md">
              <p className="text-[10px] uppercase tracking-widest text-navy/60 font-bold">
                Filter map
              </p>
              <ul className="mt-3 grid grid-cols-2 gap-2 text-sm">
                {mapLegend.map((m) => (
                  <li key={m.label} className="flex items-center gap-2 text-navy">
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: m.color }}
                      aria-hidden
                    />
                    {m.label}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 — Why It Matters */}
      <section
        className="py-24 lg:py-28 bg-navy text-cream border-t border-navy-800"
        data-testid="community-why-section"
      >
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 text-center">
          <p className="eyebrow text-gold">Why it matters</p>
          <h2 className="mt-4 font-display font-black text-cream tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-[3.4rem]">
            Building stronger <span className="text-gold-light">communities</span> through stronger connections.
          </h2>
          <div className="mt-6 h-[3px] w-16 bg-gold rounded-full mx-auto" aria-hidden />
          <p className="mt-8 text-lg lg:text-xl text-cream/80 leading-relaxed">
            A successful transition isn&rsquo;t measured only by employment or benefits. It is
            measured by <span className="text-cream font-semibold">belonging</span>. WHO helps
            people discover opportunities to connect with neighbors, support local businesses,
            attend events, volunteer, and become active members of their communities.
          </p>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="py-20 lg:py-24 bg-cream border-t border-borderSubtle" data-testid="community-cta-section">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 text-center">
          <p className="eyebrow text-gold-deep">Ready to explore?</p>
          <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.02] text-4xl sm:text-5xl">
            Your community is <span className="text-gold-deep">waiting</span>.
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <a
              href="#discovery"
              data-testid="footer-cta-explore-btn"
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-6 py-3.5 rounded-md transition-colors"
            >
              Explore Your Community <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
            </a>
            <Link
              to="/solution"
              data-testid="footer-cta-resources-btn"
              className="inline-flex items-center gap-2 border border-navy/25 hover:border-navy hover:bg-navy hover:text-cream text-navy font-semibold px-6 py-3.5 rounded-md transition-colors"
            >
              Find Resources
            </Link>
            <Link
              to="/contact"
              data-testid="footer-cta-join-btn"
              className="inline-flex items-center gap-2 border border-navy/25 hover:border-navy hover:bg-navy hover:text-cream text-navy font-semibold px-6 py-3.5 rounded-md transition-colors"
            >
              Join WHO
            </Link>
          </div>

          <p className="mt-14 font-display font-black text-3xl lg:text-4xl text-gold-deep leading-none">
            If not you, then WHO.
          </p>
        </div>
      </section>

      <CtaBanner
        eyebrow="Bring WHO to your community"
        title="Partner with us to build coordinated, connected communities."
        primaryText="Become a Partner"
        primaryTo="/partners"
        secondaryText="Meet WHO Guides"
        secondaryTo="/platform"
      />
    </div>
  );
}

/* ---------- Data ---------- */

const communityLifeCards = [
  { title: "Entertainment", desc: "Movies, concerts, live shows, and local events.", Icon: Film },
  { title: "Recreation & Leisure", desc: "Parks, trails, fitness, and outdoor adventures.", Icon: Bike },
  { title: "Events & Happenings", desc: "Festivals, workshops, and community gatherings.", Icon: Calendar },
  { title: "Veteran-Owned Businesses", desc: "Support the businesses that support you.", Icon: Store },
  { title: "Retail & Discounts", desc: "Local shops and military discounts nearby.", Icon: ShoppingBag },
  { title: "Dining", desc: "Restaurants, coffee shops, and local favorites.", Icon: Utensils },
  { title: "Arts & Culture", desc: "Theater, galleries, music, and historic sites.", Icon: Palette },
  { title: "Community Organizations", desc: "Networking, mentorship, and volunteering.", Icon: Users },
];

const discoveryFilters = [
  "Today",
  "This Week",
  "Family Friendly",
  "Free",
  "Veteran Events",
  "Indoor",
  "Outdoor",
];

const discoveryCards = [
  {
    title: "Pirates Military Appreciation Night",
    date: "Fri · 7:05 PM",
    distance: "2.4 mi",
    category: "Sports",
    tone: "gold",
    Icon: Sparkles,
  },
  {
    title: "Veterans Hiking Group",
    date: "Sat · 9:00 AM",
    distance: "6.1 mi",
    category: "Recreation",
    tone: "navy",
    Icon: Trees,
  },
  {
    title: "Coffee with Veterans",
    date: "Wed · 8:00 AM",
    distance: "0.9 mi",
    category: "Community",
    tone: "gold",
    Icon: Coffee,
  },
  {
    title: "Entrepreneurship Workshop",
    date: "Thu · 6:00 PM",
    distance: "3.2 mi",
    category: "Education",
    tone: "navy",
    Icon: Briefcase,
  },
  {
    title: "Strip District Farmers Market",
    date: "Sat · 8:00 AM",
    distance: "1.5 mi",
    category: "Local",
    tone: "gold",
    Icon: ShoppingBag,
  },
  {
    title: "Veteran-Owned Restaurant · Duty",
    date: "Open now",
    distance: "0.7 mi",
    category: "Dining",
    tone: "navy",
    Icon: Utensils,
  },
  {
    title: "Three Rivers Music Festival",
    date: "Sun · 12:00 PM",
    distance: "1.8 mi",
    category: "Arts",
    tone: "gold",
    Icon: Music,
  },
  {
    title: "Food Bank Volunteer Shift",
    date: "Sat · 10:00 AM",
    distance: "4.5 mi",
    category: "Volunteer",
    tone: "navy",
    Icon: HeartHandshake,
  },
  {
    title: "Pittsburgh Veterans Network",
    date: "Tue · 5:30 PM",
    distance: "2.0 mi",
    category: "Networking",
    tone: "gold",
    Icon: Users,
  },
];

const categoryGroups = [
  {
    title: "Entertainment",
    Icon: Film,
    items: ["Movies", "Concerts", "Sports", "Museums", "Military Appreciation Events", "Festivals"],
  },
  {
    title: "Recreation & Leisure",
    Icon: Bike,
    items: ["Parks", "Trails", "Fishing", "Hiking", "Camping", "Golf", "Fitness", "Outdoor Activities"],
  },
  {
    title: "Dining",
    Icon: Utensils,
    items: ["Restaurants", "Coffee Shops", "Local Favorites", "Veteran-Owned Restaurants", "Food Trucks"],
  },
  {
    title: "Retail",
    Icon: ShoppingBag,
    items: ["Veteran-Owned Businesses", "Military Discounts", "Shopping", "Local Markets"],
  },
  {
    title: "Arts & Culture",
    Icon: Palette,
    items: ["Performing Arts", "Theater", "Museums", "Historic Sites", "Art Galleries", "Music"],
  },
  {
    title: "Events",
    Icon: Calendar,
    items: [
      "Networking",
      "Community Events",
      "Holiday Celebrations",
      "Educational Workshops",
      "Job Fairs",
      "Professional Conferences",
    ],
  },
  {
    title: "Volunteerism",
    Icon: HeartHandshake,
    items: ["Volunteer Opportunities", "Mentorship", "Community Service", "Nonprofit Organizations"],
  },
  {
    title: "Civic Life",
    Icon: Users,
    items: ["Faith Communities", "Civic Groups", "Neighborhood Associations", "Local Government"],
  },
  {
    title: "Growth",
    Icon: GraduationCap,
    items: ["Workshops", "Classes", "Mentorship", "Career Development"],
  },
];

const aiRecommendations = [
  { title: "Veteran Entrepreneur Meetup", meta: "Thu · 6:00 PM · Southside", tag: "Business", icon: Briefcase },
  { title: "Pittsburgh Jazz Festival", meta: "Sat · 12:00 PM · Downtown", tag: "Music", icon: Music },
  { title: "Volunteer at the Food Bank", meta: "Sat · 10:00 AM · North Side", tag: "Volunteer", icon: HeartHandshake },
  { title: "Steelers Alumni Military Event", meta: "Sun · 4:00 PM · Heinz Field", tag: "Sports", icon: Sparkles },
  { title: "Bonsai Workshop", meta: "Sun · 2:00 PM · Phipps Conservatory", tag: "Hobby", icon: Trees },
  { title: "Coffee with Veterans", meta: "Wed · 8:00 AM · Squirrel Hill", tag: "Community", icon: Coffee },
];

const mapLegend = [
  { label: "Veteran Orgs", color: "#C6934C" },
  { label: "Restaurants", color: "#DDB27C" },
  { label: "Healthcare", color: "#8FB4CF" },
  { label: "Entertainment", color: "#B58AC9" },
  { label: "Events", color: "#F6C066" },
  { label: "Shopping", color: "#94C6B0" },
  { label: "Parks", color: "#7FA97C" },
  { label: "Volunteer", color: "#E28A6C" },
  { label: "Training", color: "#6C8CB8" },
  { label: "Businesses", color: "#C6934C" },
];

/* ---------- Sub-components ---------- */

function LifeCard({ title, desc, Icon }) {
  return (
    <div
      className="group p-6 bg-white border border-borderSubtle rounded-xl hover:border-gold hover:shadow-md transition-all duration-300"
      data-testid={`life-card-${title.toLowerCase().replace(/\W+/g, "-")}`}
    >
      <div className="w-11 h-11 rounded-lg bg-gold/10 flex items-center justify-center border border-gold/30 group-hover:bg-gold/20 transition-colors">
        <Icon className="w-5 h-5 text-gold-deep" />
      </div>
      <h3 className="mt-5 font-display font-bold text-navy text-lg">{title}</h3>
      <p className="mt-2 text-sm text-slateSoft leading-relaxed">{desc}</p>
      <button className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-deep hover:text-gold transition-colors">
        Explore <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function EventCard({ title, date, distance, category, tone, Icon }) {
  const bg = tone === "gold" ? "from-gold/30 via-gold/10 to-navy-deep" : "from-navy-800 via-navy-deep to-navy-deep";
  return (
    <article
      className="group rounded-xl bg-white/[0.04] border border-white/10 overflow-hidden hover:border-gold/50 transition-colors"
      data-testid={`event-card-${title.toLowerCase().replace(/\W+/g, "-")}`}
    >
      <div className={`relative h-32 bg-gradient-to-br ${bg} flex items-end p-4`}>
        <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest bg-navy-deep/70 text-gold-light px-2 py-1 rounded-sm font-bold">
          {category}
        </span>
        <Icon className="w-16 h-16 text-white/25 absolute right-4 top-4" strokeWidth={1.2} />
        <p className="text-cream text-sm font-medium">{date}</p>
      </div>
      <div className="p-5">
        <h3 className="font-display font-bold text-cream text-lg leading-snug">{title}</h3>
        <p className="mt-1 text-xs text-cream/60 flex items-center gap-1.5">
          <MapPin className="w-3 h-3" /> {distance}
        </p>
        <div className="mt-4 flex items-center gap-2">
          <IconBtn label="Save"><Bookmark className="w-3.5 h-3.5" /></IconBtn>
          <IconBtn label="Share"><Share2 className="w-3.5 h-3.5" /></IconBtn>
          <IconBtn label="Directions"><Navigation className="w-3.5 h-3.5" /></IconBtn>
        </div>
      </div>
    </article>
  );
}

function IconBtn({ children, label }) {
  return (
    <button
      className="inline-flex items-center gap-1 text-xs text-cream/70 hover:text-gold transition-colors border border-white/12 hover:border-gold/50 rounded-md px-2.5 py-1.5"
      data-testid={`event-action-${label.toLowerCase()}`}
    >
      {children}
      {label}
    </button>
  );
}

function CategoryGroup({ title, Icon, items }) {
  return (
    <div
      className="p-6 bg-white border border-borderSubtle rounded-xl hover:border-gold/50 hover:shadow-md transition-all"
      data-testid={`category-group-${title.toLowerCase().replace(/\W+/g, "-")}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-navy text-gold flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
        <h3 className="font-display font-bold text-navy text-lg">{title}</h3>
      </div>
      <ul className="mt-4 flex flex-wrap gap-1.5">
        {items.map((it) => (
          <li
            key={it}
            className="text-xs text-navy/80 bg-cream border border-borderSubtle rounded-full px-3 py-1 hover:bg-gold/10 hover:border-gold/40 transition-colors cursor-pointer"
          >
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function MapMockup() {
  // Stylized SVG "map" — abstract Pittsburgh three-rivers geometry with pins.
  const pins = [
    { x: 22, y: 30, color: "#C6934C" },
    { x: 38, y: 22, color: "#DDB27C" },
    { x: 48, y: 44, color: "#8FB4CF" },
    { x: 60, y: 30, color: "#B58AC9" },
    { x: 30, y: 58, color: "#F6C066" },
    { x: 44, y: 66, color: "#94C6B0" },
    { x: 68, y: 62, color: "#7FA97C" },
    { x: 76, y: 42, color: "#E28A6C" },
    { x: 82, y: 30, color: "#6C8CB8" },
    { x: 55, y: 74, color: "#C6934C" },
    { x: 18, y: 72, color: "#DDB27C" },
    { x: 88, y: 68, color: "#B58AC9" },
  ];
  return (
    <svg viewBox="0 0 100 60" className="w-full h-[480px] block" preserveAspectRatio="xMidYMid slice" aria-hidden>
      <defs>
        <linearGradient id="mapBg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0F2138" />
          <stop offset="100%" stopColor="#0A1928" />
        </linearGradient>
        <linearGradient id="riverGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#1E3A5F" />
          <stop offset="100%" stopColor="#0F2138" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="100" height="60" fill="url(#mapBg)" />

      {/* street grid */}
      {Array.from({ length: 14 }).map((_, i) => (
        <line key={`h${i}`} x1="0" y1={i * 4.3} x2="100" y2={i * 4.3} stroke="#1B3050" strokeWidth="0.15" />
      ))}
      {Array.from({ length: 20 }).map((_, i) => (
        <line key={`v${i}`} x1={i * 5} y1="0" x2={i * 5} y2="60" stroke="#1B3050" strokeWidth="0.15" />
      ))}

      {/* rivers — the Point (three-rivers geometry) */}
      <path
        d="M0 40 Q 25 34 45 42 T 100 44"
        stroke="url(#riverGrad)"
        strokeWidth="2.4"
        fill="none"
        opacity="0.9"
      />
      <path
        d="M0 20 Q 20 26 42 32 T 88 30"
        stroke="url(#riverGrad)"
        strokeWidth="1.8"
        fill="none"
        opacity="0.8"
      />
      <path
        d="M45 42 Q 55 38 65 26"
        stroke="url(#riverGrad)"
        strokeWidth="1.6"
        fill="none"
        opacity="0.7"
      />

      {/* landmark rings */}
      <circle cx="46" cy="38" r="4.5" fill="none" stroke="#C6934C" strokeOpacity="0.35" strokeWidth="0.25" />
      <circle cx="46" cy="38" r="8" fill="none" stroke="#C6934C" strokeOpacity="0.2" strokeWidth="0.2" />

      {/* pins */}
      {pins.map((p, i) => (
        <g key={i} transform={`translate(${p.x} ${p.y})`}>
          <circle r="1.6" fill={p.color} stroke="#0A1928" strokeWidth="0.35" />
          <circle r="0.6" fill="#0A1928" />
        </g>
      ))}

      {/* label — pointe/downtown */}
      <text x="46" y="52" fontSize="1.6" fill="#DDB27C" fontFamily="'Chivo', sans-serif" fontWeight="700" letterSpacing="0.4" textAnchor="middle" opacity="0.85">
        DOWNTOWN PITTSBURGH
      </text>
    </svg>
  );
}
